// DischargedDetailsModal.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MemberViewModal from "../Member/MemberViewModal";
import {
  faTimes,
  faUser,
  faHospital,
  faBuilding,
  faShieldAlt,
  faFileInvoice,
  faEye,
  faDownload,
  faCalculator,
  faMoneyBillWave,
  faClock,
  faCheckCircle,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { BASE_URL } from "../../config";

const MySwal = withReactContent(Swal);

const DischargedDetailsModal = ({ isOpen, onClose, transNo }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [isViewMemberModalOpen, setIsViewMemberModalOpen] = useState(false);
  //   const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [showAffectedTables, setShowAffectedTables] = useState(false);

  useEffect(() => {
    if (isOpen && transNo) {
      fetchDischargedDetails();
    }
  }, [isOpen, transNo]);

  const fetchDischargedDetails = async () => {
    setLoading(true);
    try {
      // const response = await axios.get(`${BASE_URL}/admission-discharges`, {
      //     params,
      //     headers: {
      //         Authorization: `Bearer ${localStorage.getItem(
      //         "authentication/token"
      //         )}`,
      //     },
      //     });
      const response = await fetch(
        `${BASE_URL}/admission-discharges/${transNo}/discharged-details`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch discharged details");
      }

      const result = await response.json();
      if (result.status === "success") {
        setData(result.data);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(amount || 0);
  };

  const isValidDateValue = (value) => {
    if (!value) return false;
    const trimmed = String(value).trim();
    if (
      trimmed === "" ||
      trimmed === "0000-00-00" ||
      trimmed === "0000-00-00 00:00:00"
    ) {
      return false;
    }
    const date = new Date(trimmed);
    return !Number.isNaN(date.getTime());
  };

  const formatDate = (dateString) => {
    if (!isValidDateValue(dateString)) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: faUser },
    { id: "details", label: "Transaction Details", icon: faFileInvoice },
    { id: "rnb", label: "Room & Board", icon: faHospital },
    { id: "physicians", label: "Physicians", icon: faUser },
    { id: "surgical", label: "Surgical", icon: faShieldAlt },
    { id: "misc", label: "Miscellaneous", icon: faCalculator },
    { id: "files", label: "Uploaded Files", icon: faDownload },
    { id: "summary", label: "Summary Preview", icon: faMoneyBillWave },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-indigo-600 text-white p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Discharged Details</h2>
              <p className="text-indigo-100">Transaction: {transNo}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>
          </div>

          {/* Tabs */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-indigo-500 text-indigo-600 bg-white"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <FontAwesomeIcon icon={tab.icon} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <FontAwesomeIcon
                  icon={faClock}
                  spin
                  size="2x"
                  className="text-indigo-500"
                />
                <span className="ml-2 text-gray-600">
                  Loading discharged details...
                </span>
              </div>
            ) : data ? (
              <div className="space-y-6">
                {/* Overview Tab */}
                {activeTab === "overview" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Transaction Information
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Transaction No:</span>
                          <span className="font-medium">
                            {data.transaction?.trans_no}
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Member ID:</span>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">
                              {data.transaction?.member_id}
                            </span>
                          </div>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Member:</span>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">
                              {data.transaction?.member_name}
                            </span>

                            <button
                              onClick={() => {
                                setSelectedMemberId(
                                  data.transaction?.member_id
                                );
                                setIsViewMemberModalOpen(true);
                              }}
                              className="text-indigo-600 hover:text-indigo-800 text-sm underline"
                            >
                              View Member Info
                            </button>
                          </div>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">Provider:</span>
                          <span className="font-medium">
                            {data.transaction?.provider_name}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Corporate:</span>
                          <span className="font-medium">
                            {data.transaction?.corp_name}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className="font-medium">
                            {data.transaction?.status_desc}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Dates
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Admission:</span>
                          <span className="font-medium">
                            {formatDate(data.transaction?.admission_date)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Discharge:</span>
                          <span className="font-medium">
                            {formatDate(data.transaction?.discharged_date)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">SOA Received:</span>
                          <span className="font-medium">
                            {formatDate(
                              data.computation?.final_soa_received_datetime
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transaction Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Transaction Computation
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <span className="text-gray-600">Hospital Bill:</span>
                          <div className="font-medium">
                            {formatCurrency(data.computation?.hospital_bill)}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            PhilHealth Bill:
                          </span>
                          <div className="font-medium">
                            {formatCurrency(data.computation?.philhealth_bill)}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Covered Amount:</span>
                          <div className="font-medium">
                            {formatCurrency(data.computation?.covered_amount)}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Excess:</span>
                          <div className="font-medium">
                            {formatCurrency(data.computation?.excess)}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Member Share:</span>
                          <div className="font-medium">
                            {formatCurrency(
                              data.computation?.member_share_amount
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            Guaranteed Amount:
                          </span>
                          <div className="font-medium">
                            {formatCurrency(
                              data.computation?.guaranteed_amount
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* RNB Tab */}
                {activeTab === "rnb" && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Room & Board Details
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Type
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Room No
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Days
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Rate
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Covered
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {data.rnb_rows?.map((row, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm">
                                {row.rnb_desc}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {row.room_no}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {row.no_of_days}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {formatCurrency(row.room_rate)}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {formatCurrency(row.covered)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Physicians Tab */}
                {activeTab === "physicians" && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Physician Details
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Name
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Role
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Amount
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              PhilHealth
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {data.physician_rows?.map((row, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm">
                                {row.physician_name}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {row.specialization}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {formatCurrency(row.amount)}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {formatCurrency(row.philhealth)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Surgical Tab */}
                {activeTab === "surgical" && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Surgical Details
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Remarks
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              RUV ID
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              RUV Description
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Amount
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              PhilHealth
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Operating Room Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {data.surgical_rows?.map((row, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm">
                                {row.remarks}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {row.ruv_code}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {row.rvu_description}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {formatCurrency(row.amount)}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {formatCurrency(row.philhealth)}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {formatCurrency(row.or_amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Miscellaneous Tab */}
                {activeTab === "misc" && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Miscellaneous Details
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            {/* <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">CPT Code</th> */}
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              (Particulars) Descriptions
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Amount
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              PhilHealth
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {data.misc_rows?.map((row, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm">
                                {row.cpt_code}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {formatCurrency(row.amount)}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                {formatCurrency(row.philhealth)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Uploaded Files Tab */}
                {activeTab === "files" && (
                  <div className="space-y-6">
                    {/* SOA Files */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Attach Final SOA Files
                      </h3>
                      {data.soa_files?.length > 0 ? (
                        <div className="space-y-2">
                          {data.soa_files.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                            >
                              <span className="text-sm font-medium">
                                {file.original_file_name}
                              </span>
                              <a
                                href={file.signed_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1"
                              >
                                <FontAwesomeIcon icon={faEye} />
                                <span>View</span>
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500">No SOA files uploaded</p>
                      )}
                    </div>

                    {/* Proof of Guarantee Files */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Attached Proof of Guarantee
                      </h3>
                      {data.proof_files?.length > 0 ? (
                        <div className="space-y-2">
                          {data.proof_files.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                            >
                              <span className="text-sm font-medium">
                                {file.original_file_name}
                              </span>
                              <a
                                href={file.signed_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1"
                              >
                                <FontAwesomeIcon icon={faEye} />
                                <span>View</span>
                              </a>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500">
                          No proof of guarantee files uploaded
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary Preview Tab */}
                {activeTab === "summary" && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Discharge Summary Preview
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">
                            Totals
                          </h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span>Total R&B:</span>
                              <span className="font-medium">
                                {formatCurrency(
                                  data.summary_preview?.total_rnb
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Total Physician:</span>
                              <span className="font-medium">
                                {formatCurrency(
                                  data.summary_preview?.total_physician
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Total Surgical:</span>
                              <span className="font-medium">
                                {formatCurrency(
                                  data.summary_preview?.total_surgical
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Total Miscellaneous:</span>
                              <span className="font-medium">
                                {formatCurrency(
                                  data.summary_preview?.total_misc
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span className="font-semibold">
                                Total Incurred:
                              </span>
                              <span className="font-semibold">
                                {formatCurrency(
                                  data.summary_preview?.total_incurred
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Total PhilHealth:</span>
                              <span className="font-medium">
                                {formatCurrency(
                                  data.summary_preview?.total_philhealth
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900">
                            Coverage & Limits
                          </h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span>Hospital Bill:</span>
                              <span className="font-medium">
                                {formatCurrency(
                                  data.summary_preview?.hospital_bill
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Covered Amount:</span>
                              <span className="font-medium">
                                {formatCurrency(
                                  data.summary_preview?.covered_amount
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Excess:</span>
                              <span className="font-medium">
                                {formatCurrency(data.summary_preview?.excess)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Member Share:</span>
                              <span className="font-medium">
                                {formatCurrency(
                                  data.summary_preview?.member_share
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Guaranteed Amount:</span>
                              <span className="font-medium">
                                {formatCurrency(
                                  data.summary_preview?.guaranteed_amount
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>

          {/* FOR I.T Reference Support Discharged Affected Database Table List - Accordion */}
          <div className="mt-6">
            <div className="bg-white rounded-lg shadow-sm p-0 text-sm overflow-hidden border">
              <button
                type="button"
                onClick={() => setShowAffectedTables((s) => !s)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
                aria-expanded={showAffectedTables}
              >
                <div>
                  <h3 className="font-medium">
                    FOR I.T Reference Support Discharged Affected Database Table
                    List
                  </h3>
                  <p className="text-xs text-gray-500">
                    Click to {showAffectedTables ? "collapse" : "expand"}
                  </p>
                </div>
                <span
                  className={`transform transition-transform duration-200 ${
                    showAffectedTables ? "rotate-180" : "rotate-0"
                  }`}
                >
                  <svg
                    className="w-4 h-4 text-gray-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </button>

              <div
                className={`${
                  showAffectedTables ? "block" : "hidden"
                } border-t p-4 bg-white text-gray-700 max-h-[28vh] overflow-y-auto`}
              >
                <ul className="list-disc list-inside space-y-1">
                  <li>member</li>
                  <li>provider</li>
                  <li>corporate</li>
                  <li>plan_benefits</li>
                  <li>plan</li>
                  <li>-transactions</li>
                  <li>-trans_computation</li>
                  <li>-trans_summary</li>
                  <li>-loa_trans</li>
                  <li>-claims_trans</li>
                  <li>-loa_physician</li>
                  <li>-claims_physician</li>
                  <li>-trans_rnb</li>
                  <li>-trans_cpt</li>
                  <li>-trans_surgical</li>
                  <li>-trans_ruv</li>
                  <li>-loa_icd</li>
                </ul>
              </div>
            </div>
          </div>
          {/* Member View Modal */}
          {/* External Modals */}
          {isViewMemberModalOpen && (
            <MemberViewModal
              isOpen={isViewMemberModalOpen}
              onClose={() => setIsViewMemberModalOpen(false)}
              memberId={selectedMemberId}
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DischargedDetailsModal;
